<template>
  <v-container fluid class="logs-container">
    <v-row>
      <v-col cols="12">
        <v-card class="logs-card">
          <v-card-title class="d-flex align-center pa-4">
            <v-icon size="24" color="primary" class="mr-2">mdi-console</v-icon>
            <span class="text-h5">Logs do Sistema</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewMode" color="primary" variant="outlined">
              <v-btn value="stats">
                <v-icon>mdi-chart-line</v-icon>
                Estatísticas
              </v-btn>
              <v-btn value="logs">
                <v-icon>mdi-format-list-bulleted</v-icon>
                Logs
              </v-btn>
            </v-btn-toggle>
          </v-card-title>

          <!-- Vista de Estatísticas -->
          <div v-if="viewMode === 'stats'">
            <v-card-text class="pa-4">
              <!-- Filtros -->
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-select
                    v-model="selectedHours"
                    :items="hoursOptions"
                    label="Período"
                    variant="outlined"
                    density="compact"
                    @update:modelValue="loadStats"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    @click="loadStats"
                    :loading="loading"
                    block
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Atualizar
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Cards de Estatísticas -->
              <v-row class="mb-4">
                <v-col cols="12" sm="6" md="3">
                  <v-card class="stat-card" color="blue-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number blue--text text-h4">{{ stats.total_requests }}</div>
                      <div class="stat-label text-caption">Requisições</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card class="stat-card" color="red-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number red--text text-h4">{{ stats.total_errors }}</div>
                      <div class="stat-label text-caption">Erros</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card class="stat-card" color="green-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number green--text text-h4">{{ stats.error_rate }}%</div>
                      <div class="stat-label text-caption">Taxa de Erro</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card class="stat-card" color="orange-lighten-5">
                    <v-card-text class="text-center">
                      <div class="stat-number orange--text text-h4">{{ stats.avg_response_time }}ms</div>
                      <div class="stat-label text-caption">Tempo Médio</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Top Erros -->
              <v-row v-if="Object.keys(stats.top_errors).length > 0">
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title class="text-h6">
                      <v-icon color="red" class="mr-2">mdi-alert-circle</v-icon>
                      Erros Mais Frequentes
                    </v-card-title>
                    <v-card-text>
                      <v-list density="compact">
                        <v-list-item
                          v-for="(count, error) in stats.top_errors"
                          :key="error"
                        >
                          <template v-slot:prepend>
                            <v-icon color="red" size="20">mdi-alert</v-icon>
                          </template>
                          <v-list-item-title class="text-wrap">{{ error }}</v-list-item-title>
                          <v-list-item-subtitle>{{ count }} ocorrências</v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Erros Recentes -->
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title class="text-h6">
                      <v-icon color="orange" class="mr-2">mdi-clock</v-icon>
                      Erros Recentes
                    </v-card-title>
                    <v-card-text>
                      <v-list density="compact">
                        <v-list-item
                          v-for="error in stats.recent_errors"
                          :key="error.timestamp"
                        >
                          <template v-slot:prepend>
                            <v-icon color="orange" size="20">mdi-alert-outline</v-icon>
                          </template>
                          <v-list-item-title class="text-wrap">{{ error.message }}</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ formatDateTime(error.timestamp) }} - {{ error.context }}
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </div>

          <!-- Vista de Logs -->
          <div v-if="viewMode === 'logs'">
            <v-card-text class="pa-4">
              <!-- Filtros -->
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-select
                    v-model="logType"
                    :items="logTypeOptions"
                    label="Tipo de Log"
                    variant="outlined"
                    density="compact"
                    @update:modelValue="loadLogs"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="logLimit"
                    :items="limitOptions"
                    label="Quantidade"
                    variant="outlined"
                    density="compact"
                    @update:modelValue="loadLogs"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    @click="loadLogs"
                    :loading="loading"
                    block
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Carregar Logs
                  </v-btn>
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    color="red"
                    variant="outlined"
                    @click="clearLogs"
                    :loading="clearing"
                    block
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Limpar Logs
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Lista de Logs -->
              <v-card>
                <v-card-text class="pa-0">
                  <v-data-table
                    :items="logs"
                    :headers="logHeaders"
                    :loading="loading"
                    density="compact"
                    items-per-page="50"
                    class="logs-table"
                  >
                    <template v-slot:item.timestamp="{ item }">
                      <span class="text-caption">{{ formatDateTime(item.timestamp) }}</span>
                    </template>
                    
                    <template v-slot:item.type="{ item }">
                      <v-chip
                        :color="getTypeColor(item.type)"
                        size="x-small"
                        variant="flat"
                      >
                        {{ item.type }}
                      </v-chip>
                    </template>
                    
                    <template v-slot:item.status="{ item }">
                      <v-chip
                        v-if="item.status"
                        :color="item.status >= 400 ? 'red' : 'green'"
                        size="x-small"
                        variant="flat"
                      >
                        {{ item.status }}
                      </v-chip>
                    </template>
                    
                    <template v-slot:item.response_time_ms="{ item }">
                      <span v-if="item.response_time_ms" class="text-caption">
                        {{ item.response_time_ms }}ms
                      </span>
                    </template>
                    
                    <template v-slot:item.details="{ item }">
                      <v-btn
                        icon="mdi-eye"
                        size="x-small"
                        variant="text"
                        @click="showLogDetails(item)"
                      ></v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-card-text>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog de Detalhes -->
    <v-dialog v-model="detailsDialog" max-width="800px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information</v-icon>
          Detalhes do Log
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <strong>Data/Hora:</strong><br>
                {{ formatDateTime(selectedLog.timestamp) }}
              </v-col>
              <v-col cols="12" sm="6">
                <strong>Tipo:</strong><br>
                <v-chip :color="getTypeColor(selectedLog.type)" size="small">
                  {{ selectedLog.type }}
                </v-chip>
              </v-col>
            </v-row>
            
            <v-row v-if="selectedLog.method">
              <v-col cols="12" sm="6">
                <strong>Método:</strong> {{ selectedLog.method }}
              </v-col>
              <v-col cols="12" sm="6">
                <strong>Endpoint:</strong> {{ selectedLog.endpoint }}
              </v-col>
            </v-row>
            
            <v-row v-if="selectedLog.message">
              <v-col cols="12">
                <strong>Mensagem:</strong><br>
                {{ selectedLog.message }}
              </v-col>
            </v-row>
            
            <v-row v-if="selectedLog.params && Object.keys(selectedLog.params).length > 0">
              <v-col cols="12">
                <strong>Parâmetros:</strong><br>
                <pre class="log-json">{{ JSON.stringify(selectedLog.params, null, 2) }}</pre>
              </v-col>
            </v-row>
            
            <v-row v-if="selectedLog.trace">
              <v-col cols="12">
                <strong>Stack Trace:</strong><br>
                <pre class="log-trace">{{ selectedLog.trace.join('\n') }}</pre>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="detailsDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'

export default {
  name: 'SistemaLogs',
  setup() {
    const viewMode = ref('stats')
    const loading = ref(false)
    const clearing = ref(false)
    const stats = ref({
      total_requests: 0,
      total_errors: 0,
      total_events: 0,
      error_rate: 0,
      avg_response_time: 0,
      top_errors: {},
      recent_errors: []
    })
    const logs = ref([])
    const selectedLog = ref({})
    const detailsDialog = ref(false)
    
    // Filtros
    const selectedHours = ref(24)
    const logType = ref('all')
    const logLimit = ref(100)
    
    const hoursOptions = [
      { title: 'Última 1 hora', value: 1 },
      { title: 'Últimas 6 horas', value: 6 },
      { title: 'Últimas 24 horas', value: 24 },
      { title: 'Últimos 3 dias', value: 72 },
      { title: 'Última semana', value: 168 }
    ]
    
    const logTypeOptions = [
      { title: 'Todos', value: 'all' },
      { title: 'Requisições', value: 'requests' },
      { title: 'Erros', value: 'errors' }
    ]
    
    const limitOptions = [
      { title: '50', value: 50 },
      { title: '100', value: 100 },
      { title: '200', value: 200 },
      { title: '500', value: 500 }
    ]
    
    const logHeaders = [
      { title: 'Data/Hora', key: 'timestamp', width: '150px' },
      { title: 'Tipo', key: 'type', width: '100px' },
      { title: 'Contexto', key: 'context' },
      { title: 'Status', key: 'status', width: '80px' },
      { title: 'Tempo', key: 'response_time_ms', width: '80px' },
      { title: 'Detalhes', key: 'details', width: '80px', sortable: false }
    ]
    
    const loadStats = async () => {
      loading.value = true
      try {
        const response = await axios.get(`/api/logs-view.php?action=stats&hours=${selectedHours.value}`)
        stats.value = response.data
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error)
      } finally {
        loading.value = false
      }
    }
    
    const loadLogs = async () => {
      loading.value = true
      try {
        const response = await axios.get(`/api/logs-view.php?action=logs&type=${logType.value}&limit=${logLimit.value}`)
        logs.value = response.data
      } catch (error) {
        console.error('Erro ao carregar logs:', error)
      } finally {
        loading.value = false
      }
    }
    
    const clearLogs = async () => {
      if (!confirm('Tem certeza que deseja limpar logs antigos?')) return
      
      clearing.value = true
      try {
        await axios.delete('/api/logs-view.php?action=clear&days=7')
        alert('Logs antigos removidos com sucesso!')
        loadStats()
        loadLogs()
      } catch (error) {
        console.error('Erro ao limpar logs:', error)
        alert('Erro ao limpar logs')
      } finally {
        clearing.value = false
      }
    }
    
    const showLogDetails = (log) => {
      selectedLog.value = log
      detailsDialog.value = true
    }
    
    const formatDateTime = (timestamp) => {
      return new Date(timestamp).toLocaleString('pt-BR')
    }
    
    const getTypeColor = (type) => {
      switch (type) {
        case 'REQUEST': return 'blue'
        case 'ERROR': return 'red'
        case 'EVENT': return 'green'
        default: return 'grey'
      }
    }
    
    onMounted(() => {
      loadStats()
      loadLogs()
    })
    
    return {
      viewMode,
      loading,
      clearing,
      stats,
      logs,
      selectedLog,
      detailsDialog,
      selectedHours,
      logType,
      logLimit,
      hoursOptions,
      logTypeOptions,
      limitOptions,
      logHeaders,
      loadStats,
      loadLogs,
      clearLogs,
      showLogDetails,
      formatDateTime,
      getTypeColor
    }
  }
}
</script>

<style scoped>
.logs-container {
  background: #f5f5f5;
  min-height: 100vh;
}

.logs-card {
  margin: 0;
}

.stat-card {
  height: 100px;
}

.stat-number {
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.logs-table {
  font-size: 12px;
}

.log-json {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  font-size: 11px;
  max-height: 200px;
  overflow-y: auto;
}

.log-trace {
  background: #fff3f3;
  padding: 10px;
  border-radius: 4px;
  font-size: 10px;
  max-height: 300px;
  overflow-y: auto;
  color: #d32f2f;
}
</style>
